import {useCallback, useMemo} from "react";
import {Navigate, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import Calendar from "react-calendar";
import i18n from "i18next";
import "@src/CommonComponents/Calendar/CalendarOverride.less";
import {ChevronLeft} from "@UI/components/icons/ChevronLeft.tsx";
import {ChevronRight} from "@UI/components/icons/ChevronRight.tsx";
// Same icon as attendance history; renamed to not clash with react-calendar's Calendar.
import {Calendar as CalendarIcon} from "@UI/components/icons/Calendar.tsx";
import {ArrowPointerLeft} from "@UI/components/icons/ArrowPointerLeft.tsx";
import {Button, ButtonType} from "@UI/components/controls/Button/Button.tsx";
import {ColorNames} from "@UI/theme/generated/ColorNames.ts";
import {
    Body_12_Coral_Empasis_12,
    Body_12_Coral_Primary_Regular12,
    Body_14_Coral_Primary_Regular_14,
    Body_16_Coral_Emphasis_16,
    Title_20_Coral_Strong_emphasis_20
} from "@UI/typography/generated/typography.tsx";
import {Stack} from "@src/CommonComponents/Stack/Stack.tsx";
import {MenuLinkButton} from "@src/CommonComponents/MenuLinkButton/MenuLinkButton.tsx";
import {DataNotFoundMessage, ErrorLoadingMessage} from "@src/Pages/Views/Common.tsx";
import {RosterEmployee} from "@src/httpClient/__generated__/Api.ts";
import {apiClient} from "@src/httpClient/api.ts";
import {useCredentialsStore} from "@src/Store/userStore.ts";
import {BRANCH_FILTER_KEY} from "@src/utils/useFilters.ts";
import {formatDate, formatTime} from "@src/utils/formatDate.ts";
import {Gt, GtDatetime, LocalDTFormater} from "@src/i18n/Gt.tsx";
import cn from "classnames";
import styles from "./calendarPage.module.less";

const PAGE_LINK = "/calendar/"

// Managers and staff ("site admin") plan headcount; nobody else sees the page.
const useCanPlan = () => useCredentialsStore(x => !!(x.employee?.is_manager || x.employee?.is_staff))

// The desktop top selector uses small labels and marks the open page; the mobile menu does neither.
export const CalendarMenuButton = ({inline = false}: { inline?: boolean }) => {
    const location = useLocation()
    if (!useCanPlan()) return null
    const Label = inline ? Body_12_Coral_Empasis_12 : Body_16_Coral_Emphasis_16
    return (
        <MenuLinkButton href={PAGE_LINK} icon={<CalendarIcon size={22}/>}
                        active={inline && location.pathname.startsWith(PAGE_LINK)}>
            <Label>
                <Gt>Schedule calendar</Gt>
            </Label>
        </MenuLinkButton>
    )
}

const PersonRow = ({employee, hours}: { employee: RosterEmployee, hours?: string }) => (
    <Stack className={styles.person} gap={12} alignItems={"center"}>
        <Stack direction={"column"} grow>
            <Body_16_Coral_Emphasis_16>{employee.last_name} {employee.first_name}</Body_16_Coral_Emphasis_16>
            <Body_12_Coral_Primary_Regular12>{employee.branches.map(branch => branch.name).join("、")}</Body_12_Coral_Primary_Regular12>
        </Stack>
        {hours && <Body_14_Coral_Primary_Regular_14>{hours}</Body_14_Coral_Primary_Regular_14>}
    </Stack>
)

const DayRoster = ({date, branchesSearch, onBack}: { date: string, branchesSearch: string, onBack: () => void }) => {
    const {data, isLoading, isError} = useQuery({
        queryKey: ["schedule-roster-day", date, branchesSearch],
        queryFn: async () => {
            // The http client only sends the search string, never the query object.
            const search = new URLSearchParams(branchesSearch)
            search.set("date", date)
            return (await apiClient.api.scheduleRosterDayRetrieve({date}, {search: search.toString()})).data
        },
    })

    return (
        <Stack direction={"column"} gap={8} className={styles.day}>
            <Stack alignItems={"center"} gap={8}>
                <Button className={styles.dayBack} color={ColorNames.OBSIDIAN} type={ButtonType.text}
                        icon={<ArrowPointerLeft size={30}/>} onClick={onBack}/>
                <Title_20_Coral_Strong_emphasis_20>
                    <GtDatetime template={LocalDTFormater.LocalDateShort}>{`${date}T00:00:00`}</GtDatetime>
                    {data && ` · ${data.working.length}`}
                </Title_20_Coral_Strong_emphasis_20>
            </Stack>
            {data?.holiday && <Body_14_Coral_Primary_Regular_14>{data.holiday}</Body_14_Coral_Primary_Regular_14>}

            {isLoading && <Body_14_Coral_Primary_Regular_14><Gt>Loading</Gt>...</Body_14_Coral_Primary_Regular_14>}
            {isError && <ErrorLoadingMessage/>}
            {data && data.working.length === 0 && data.unscheduled.length === 0 &&
                <DataNotFoundMessage text={<Gt>Nobody is scheduled</Gt>}/>}

            {data?.working.map(shift => (
                <PersonRow key={shift.employee.id} employee={shift.employee}
                           hours={`${formatTime(shift.start_time)}–${formatTime(shift.end_time)}`}/>
            ))}
            {!!data?.unscheduled.length && (
                <>
                    <Stack className={styles.separator}>
                        <Body_14_Coral_Primary_Regular_14>
                            <Gt>No schedule</Gt> · {data.unscheduled.length}
                        </Body_14_Coral_Primary_Regular_14>
                    </Stack>
                    {data.unscheduled.map(employee => <PersonRow key={employee.id} employee={employee}/>)}
                </>
            )}
        </Stack>
    )
}

export const CalendarPage = () => {
    const canPlan = useCanPlan()
    const hasEmployee = useCredentialsStore(x => !!x.employee)
    const [params] = useSearchParams()
    const navigate = useNavigate()

    // ?month=YYYY-MM, never before the current month.
    const thisMonth = useMemo(() => {
        const now = new Date()
        return new Date(now.getFullYear(), now.getMonth(), 1)
    }, [])
    const month = useMemo(() => {
        const parsed = new Date(`${params.get("month")}-01T00:00:00`)
        return isNaN(parsed.getTime()) || parsed < thisMonth ? thisMonth : parsed
    }, [params, thisMonth])
    const selectedDate = params.get("date")

    const branchesSearch = useMemo(() => {
        const branches = params.getAll(BRANCH_FILTER_KEY)
        return branches.length ? new URLSearchParams({branch_ids__in: branches.join(",")}).toString() : ""
    }, [params])

    // Month changes replace history; picking a day pushes, so "back" returns to the grid.
    const go = useCallback((changes: Record<string, string | null>, replace: boolean) => {
        const p = new URLSearchParams(params)
        Object.entries(changes).forEach(([key, value]) => value === null ? p.delete(key) : p.set(key, value))
        navigate(`?${p.toString()}`, {replace})
    }, [params])

    const {data: days, isError} = useQuery({
        queryKey: ["schedule-roster", month.getFullYear(), month.getMonth(), branchesSearch],
        queryFn: async () => {
            const year = month.getFullYear()
            const monthNumber = month.getMonth() + 1
            // The http client only sends the search string, never the query object.
            const search = new URLSearchParams(branchesSearch)
            search.set("year", String(year))
            search.set("month", String(monthNumber))
            return (await apiClient.api.scheduleRosterList({year, month: monthNumber}, {search: search.toString()})).data
        },
        enabled: canPlan,
    })
    const byDate = useMemo(() => new Map(days?.map(day => [day.date, day])), [days])

    if (!hasEmployee) return null
    if (!canPlan) return <Navigate to={"/check-in/"} replace/>

    return (
        <Stack className={cn(styles.root, {[styles.daySelected]: !!selectedDate})} direction={"column"} gap={16}>
            <Stack direction={"column"} className={styles.grid} gap={8}>
                <Calendar
                    calendarType={"gregory"}
                    locale={i18n.language}
                    prevLabel={<ChevronLeft/>}
                    nextLabel={<ChevronRight/>}
                    prev2Label={null}
                    next2Label={null}
                    minDetail={"month"}
                    maxDetail={"month"}
                    minDate={thisMonth}
                    showNeighboringMonth={false}
                    activeStartDate={month}
                    onActiveStartDateChange={({activeStartDate}) => activeStartDate && go({
                        month: formatDate(activeStartDate.toISOString()).slice(0, 7),
                        date: null
                    }, true)}
                    value={selectedDate ? new Date(`${selectedDate}T00:00:00`) : null}
                    onClickDay={day => go({date: formatDate(day.toISOString())}, false)}
                    // Only tomorrow onwards is planned; past days are attendance history.
                    tileDisabled={({date}) => date <= new Date()}
                    formatDay={(_, d) => d.getDate().toString()}
                    tileContent={({date}) => {
                        const day = byDate.get(formatDate(date.toISOString()))
                        // Past days and days nobody works show "-", like empty attendance history rows.
                        if (!day && date > new Date()) return null
                        return (
                            <span className={styles.count} title={day?.holiday ?? undefined}>
                                {day?.working_count || "-"}
                            </span>
                        )
                    }}
                />
                {isError && <ErrorLoadingMessage/>}
            </Stack>
            {selectedDate ? (
                <DayRoster date={selectedDate} branchesSearch={branchesSearch} onBack={() => go({date: null}, true)}/>
            ) : (
                <Stack direction={"column"} alignItems={"center"} gap={16} className={styles.placeholder}>
                    <CalendarIcon size={22} className={styles.placeholderIcon}/>
                    <Title_20_Coral_Strong_emphasis_20>
                        <Gt>Select a day in the calendar</Gt>
                    </Title_20_Coral_Strong_emphasis_20>
                </Stack>
            )}
        </Stack>
    )
}
